import { navigate } from 'gatsby'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export const STORE = {
  PREFFERRED_LANGUAGE: {
    JAPANESE: 'ja',
    ENGLISH: 'en',
  },
} as const

/**
 * Ensures type safety when working with language values
 */
export type PreferredLanguageType = (typeof STORE.PREFFERRED_LANGUAGE)[keyof typeof STORE.PREFFERRED_LANGUAGE]

export const DEFAULT_LANGUAGE: PreferredLanguageType = STORE.PREFFERRED_LANGUAGE.JAPANESE

/**
 * Type-safe store interface for language preferences
 * Used by LocalizedLink and other components for language-aware routing
 * @interface PreferredLanguagesSettingStore
 */
interface PreferredLanguagesSettingStore {
  /** Current language preference */
  preferredLanguage: PreferredLanguageType
  /** Switch to Japanese language */
  setJapanese: () => void
  /** Switch to English language */
  setEnglish: () => void
}

/**
 * Determines initial language preference based on URL path
 * Following project's URL path patterns:
 * - Japanese: Standard paths ('/', '/package/', etc.)
 * - English: Prefixed paths ('/en/', '/en/package/', etc.)
 *
 * @returns {PreferredLanguageType} Current language preference
 */
const getInitialLanguage = (): PreferredLanguageType => {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE
  const isEnglishPath = window.location.pathname.startsWith('/en/')
  return isEnglishPath ? STORE.PREFFERRED_LANGUAGE.ENGLISH : STORE.PREFFERRED_LANGUAGE.JAPANESE
}

export const redirectTo = (language: PreferredLanguageType) => {
  const currentPath = location.pathname
  const isEnglishPath = currentPath.startsWith('/en/')

  // japanese to english
  if (language === STORE.PREFFERRED_LANGUAGE.JAPANESE && isEnglishPath) {
    navigate(currentPath.replace('/en', ''))
  }
  // english to japanese
  else if (language === STORE.PREFFERRED_LANGUAGE.ENGLISH && !isEnglishPath) {
    navigate(currentPath === '/' ? '/en/' : `/en${currentPath}`)
  }
}

/**
 * Create a Zustand store for managing language preferences
 * Follows project's established patterns for:
 * - Type-safe language handling with imported types
 * - URL-based language detection and routing
 * - Persistent storage with proper hydration
 * - Clean action methods for language switching
 *
 * Used by LocalizedLink and other components to handle language-aware routing
 * and content localization.
 *
 * @example
 * ```typescript
 * const { preferredLanguage, setEnglish } = usePreferredLanguagesSettingStore()
 *
 * // Check current language
 * if (preferredLanguage === STORE.PREFFERRED_LANGUAGE.ENGLISH) {
 *   // Handle English content
 * }
 *
 * // Switch to English
 * setEnglish()
 * ```
 *
 * @returns {PreferredLanguagesSettingStore} Language preference store instance
 */
export const usePreferredLanguagesSettingStore = create<PreferredLanguagesSettingStore>()(
  persist(
    (set) => ({
      preferredLanguage: getInitialLanguage(),
      setJapanese: () => set({ preferredLanguage: STORE.PREFFERRED_LANGUAGE.JAPANESE }),
      setEnglish: () => set({ preferredLanguage: STORE.PREFFERRED_LANGUAGE.ENGLISH }),
    }),
    {
      name: 'language-preference',
      // @see  https://zustand.docs.pmnd.rs/middlewares/persist
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.preferredLanguage = getInitialLanguage()
        }
        return state
      },
    }
  )
)
